import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import axios from 'axios';
import throttle from 'lodash/throttle';
import i18next from 'i18next';
import { all, takeEvery, take, call, put, select, actionChannel, fork } from 'redux-saga/effects';
import { buffers, eventChannel, END } from 'redux-saga';
import { getConfigKey } from '@wix/da-redux-config/pkg/selectors';
import { flagEnabled } from '@wix/da-react-context/pkg/flags/redux/selectors';
import { uploadFile, uploadFailure, uploadSuccess, uploadProgress, uploadFiles, uploadFileImmediately, uploadDocument, uploadDocumentProgress, uploadDocumentSuccess, uploadDocumentFailure, cancelDocumentUpload } from './actions';
import { UploadErrorCode } from './types';
import { getUploads, getUploadsWaiting, getUploadById } from './selectors';
import { isApiError, configurePuppyOptions, getApiResponse, requestPuppy } from '@wix/da-http-client/pkg/request';
const THROTTLE_WAIT = 500;
const MAXIMUM_CONCURRENT_UPLOADS = 10;
const PCP_FOLDER_NAME = 'PCP attachments';
const DRAFTS_FOLDER_NAME = 'Saved Submissions';
const MEDIA_FOLDER_NAME = 'Media Uploads';
const fakeDeviationFetch = (options)=>{
    if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line
        const { deviationsByType } = require('@wix/da-sample-data');
        return _object_spread({}, deviationsByType.image[0], {
            deviationId: options.params.deviationid
        });
    }
};
// Simulate file upload channel events
let fakeDeviationId = 123;
function createFakeUploadFileChannel(uploadType) {
    return eventChannel((emitter)=>{
        const throttledEmitter = throttle(emitter, THROTTLE_WAIT);
        let progress = 0;
        const interval = setInterval(()=>{
            progress = Math.min(1, progress);
            throttledEmitter({
                progress
            });
            if (progress < 1) {
                return progress += Math.random() / 100;
            }
            if (window.doError) {
                emitter({
                    error: new Error('Test error')
                });
                emitter(END);
                return;
            }
            const response = uploadType === 'document' ? {
                deviationId: fakeDeviationId++,
                narfDocument: 'http://example.com/fake/doc/url',
                sprfDocument: 'http://example.com/fake/doc/url'
            } : {
                data: {
                    privateId: fakeDeviationId++
                }
            };
            emitter({
                progress: 1
            });
            emitter({
                success: true,
                response
            });
            emitter(END);
        }, 50);
        return ()=>{
            clearInterval(interval);
        };
    });
}
function* createDeviationUploadChannel({ file, deviationId, folderId, folderName, useDefaults, isPreviewUpload }) {
    const baseUrl = 'deviation/submit/upload';
    let url = deviationId ? `${baseUrl}/replace_original` : `${baseUrl}/deviation`;
    if (isPreviewUpload) {
        url = `${baseUrl}/replace_preview`;
    }
    const formData = new FormData();
    if (deviationId) {
        formData.append('deviationid', deviationId);
        if (isPreviewUpload) {
            formData.append('preview_file', file);
        } else {
            formData.append('original_file', file);
        }
    } else {
        formData.append('upload_file', file);
        formData.append('use_defaults', useDefaults);
        if (folderId) {
            formData.append('folderid', folderId);
        } else {
            formData.append('folder_name', folderName);
        }
    }
    return yield call(createUploadChannel, url, formData);
}
function* createDocumentUploadChannel(file, deviationId, documentType) {
    const url = 'deviation/submit/upload/document';
    const formData = new FormData();
    formData.append('document_file', file);
    formData.append('deviationid', `${deviationId}`);
    formData.append('type', documentType);
    return yield call(createUploadChannel, url, formData, 'document');
}
function* createUploadChannel(url, // We need a FormData object so that axios uses the form-multipart serializer
formData, uploadType = 'deviation') {
    if (process.env.NODE_ENV !== 'production') {
        const doFake = yield select(getConfigKey, 'fakeApiRequests');
        if (doFake) {
            return createFakeUploadFileChannel(uploadType);
        }
    }
    const cancelToken = axios.CancelToken.source();
    const options = yield call(configurePuppyOptions, {
        url,
        method: 'post',
        headers: {
            'Content-Type': 'multipart/form-data'
        },
        withCredentials: true,
        cancelToken: cancelToken.token
    }, 'dashared');
    for(const key in options.data){
        formData.append(key, options.data[key]);
    }
    return eventChannel((emitter)=>{
        const throttledEmitter = throttle(emitter, THROTTLE_WAIT);
        getApiResponse(_object_spread_props(_object_spread({}, options), {
            data: formData,
            onUploadProgress: (e)=>{
                if (e.lengthComputable) {
                    const progress = e.loaded / e.total;
                    throttledEmitter({
                        progress
                    });
                }
            }
        })).then((response)=>{
            if (!response || isApiError(response)) {
                // For some specific error codes we display the message coming from BE, otherwise we use a generic one
                const { errorCode, errorDescription } = response !== null && response !== void 0 ? response : {};
                const serverErrorMessage = [
                    UploadErrorCode.ITEM_COUNT_QUOTA_LIMIT_REACHED
                ].includes(errorCode) && errorDescription;
                const errorMessage = serverErrorMessage || i18next.t('uploadZone.error');
                const error = new Error(errorMessage, {
                    cause: errorDescription
                });
                emitter({
                    error
                });
                emitter(END);
                return;
            }
            emitter({
                success: true,
                response
            });
        });
        return ()=>{
            cancelToken.cancel();
        };
    }, buffers.sliding(2));
}
function* uploadFileHandler(action) {
    const { file, fileId, deviationId, folderId, autoFetchDeviationOnSuccess, isPcp, isDraft, useDefaults, onSuccess, onFailure, isPreviewUpload } = action.payload;
    // Unless folderid is specified, the folder will be set by name, which will be one of these 3:
    // - "PCP attachments" if it's a pcp asset
    // - "Saved Submissions" if it's a draft upload on studio
    // - "Media Uploads" if it's anything else (e.g. editor uploads)
    // - Stash protect folder name, as defined in `useFileUpload` hook
    const folderName = action.payload.folderName || (isDraft ? DRAFTS_FOLDER_NAME : isPcp ? PCP_FOLDER_NAME : MEDIA_FOLDER_NAME);
    const channel = yield call(createDeviationUploadChannel, {
        file,
        deviationId,
        folderId,
        folderName,
        useDefaults,
        isPreviewUpload
    });
    while(true){
        const fileUpload = yield select(getUploadById, fileId);
        if (!fileUpload) {
            // if the file was removed from store (i.e. cancelled) just bail
            return;
        }
        const { progress = 0, error, success, response } = yield take(channel);
        if (error) {
            yield put(uploadFailure(fileId, error));
            if (onFailure) {
                yield call(onFailure, {
                    fileId
                }, error);
            }
            return;
        }
        if (success) {
            const uploadedDeviationId = deviationId ? response === null || response === void 0 ? void 0 : response.deviationId : response === null || response === void 0 ? void 0 : response.privateId;
            yield put(uploadSuccess(fileId, uploadedDeviationId));
            if (onSuccess) {
                let deviation;
                if (autoFetchDeviationOnSuccess && uploadedDeviationId) {
                    const params = {
                        deviationid: uploadedDeviationId,
                        type: 'art'
                    };
                    const result = yield call(requestPuppy, {
                        method: 'get',
                        url: '/deviation/fetch',
                        params: params
                    }, fakeDeviationFetch);
                    if (result && !isApiError(result)) {
                        deviation = result;
                    }
                }
                yield call(onSuccess, {
                    fileId,
                    file,
                    deviationId: uploadedDeviationId,
                    deviation
                });
            }
            return;
        }
        yield put(uploadProgress(fileId, progress));
    }
}
function* uploadDocumentHandler({ payload: { file, deviationId, documentType, onSuccess } }) {
    const channel = yield call(createDocumentUploadChannel, file, deviationId, documentType);
    // If a cancel action for this document type is fired we close the channel
    yield fork(function*() {
        yield take((action)=>action.type === cancelDocumentUpload.toString() && action.payload.documentType === documentType);
        channel.close();
    });
    while(true){
        const { progress = 0, error, success, response } = yield take(channel);
        if (error) {
            yield put(uploadDocumentFailure(documentType, error));
            return;
        }
        if (success) {
            const documentUrl = documentType === 'narf' ? response.narfDocument : response.sprfDocument;
            yield put(uploadDocumentSuccess(documentType, documentUrl));
            yield call(onSuccess, documentUrl);
            return;
        }
        yield put(uploadDocumentProgress(documentType, progress));
    }
}
function* uploadQueueHandler(action) {
    const sequenceUploads = yield select(flagEnabled, 'submit_seq_uploads');
    const uploads = yield select(getUploads);
    const activeUploads = uploads.filter((upload)=>!upload.error);
    const openUploadSlots = sequenceUploads ? 1 - activeUploads.length : MAXIMUM_CONCURRENT_UPLOADS - activeUploads.length;
    if (openUploadSlots <= 0) {
        // do nothing, we have no space to queue more
        return;
    }
    const waitingUploads = yield select(getUploadsWaiting);
    // start downloading the appropriate amount
    yield all(waitingUploads.slice(0, openUploadSlots).map((upload)=>put(uploadFileImmediately(upload))));
}
function* uploadQueueWatcher() {
    const channel = yield actionChannel([
        uploadFile,
        uploadFiles,
        uploadSuccess,
        uploadFailure
    ]);
    while(true){
        const action = yield take(channel);
        yield call(uploadQueueHandler, action);
    }
}
export default function* saga() {
    yield all([
        takeEvery(uploadFileImmediately, uploadFileHandler),
        takeEvery(uploadDocument, uploadDocumentHandler),
        // update the queue every time something relevant happens
        uploadQueueWatcher()
    ]);
}
